import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { notification } from 'antd';
import { compose } from 'redux';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import { pending } from 'redux-saga-thunk';
import { reduxForm } from 'redux-form';
import { fromEntities, fromResource } from '../store/selectors';
import { modalHide, modalShow } from '../store/modal/actions';
import { isOpen } from '../store/modal/selectors';
import InHandTab from '../components/organisms/InHandTab';
import { createValidator, integer, minSize, required } from '../services/validation';
import {
  resourceDeleteRequest,
  resourceDeleteSuccess,
  resourceDetailReadRequest,
  resourceListReadRequest,
  resourceUpdateRequest,
} from '../store/resource/actions';
import { emptyEditRecord, setEditRecord } from '../store/updateCampaign/actions';

const openNotification = message => {
  notification.error({ message: 'Error', description: message, duration: 0 });
};

class InHandTabContainer extends Component {
  componentDidMount() {
    this.props.verifyInhand();
    this.props.clearCreativeEntities();
    this.props.getProductList().catch(e =>
      notification.error({
        message: 'Error',
        description: typeof e === 'string' ? e : 'Could not retrieve product list.',
        duration: 0,
      })
    );
  }

  componentDidUpdate = prevProps => {
    if (
      !isEqual(prevProps.inhandVerify, this.props.inhandVerify) &&
      !isEmpty(this.props.inhandVerify)
    ) {
      openNotification(this.props.inhandVerify);
    }

    if (
      !isEqual(prevProps.productList, this.props.productList) &&
      !isEmpty(this.props.productList)
    ) {
      Promise.all(this.props.productList.map(this.props.getCreativeList)).catch(e =>
        notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not retrieve product list.',
          duration: 0,
        })
      );
    }
  };

  removeProductCreativesAndProduct = productId => {
    // remove creatives here
    const list = get(this.props.creativeList, productId, []) || [];
    list.forEach(creative => {
      this.props.removeCreative(productId, creative.id);
    });
    this.props.removeProduct(productId);
  };

  render() {
    return <InHandTab {...this.props} removeProduct={this.removeProductCreativesAndProduct} />;
  }
}

const mapStateToProps = ({ modal, ...state }) => {
  const initialValues = { product: [] };

  let productList = fromEntities.getList(
    state,
    'advertiserAdInhand',
    fromResource.getList(state, 'advertiserAdInhand')
  );

  const inhandVerify = fromResource.getError(state, 'advertiserAdInhandVerify');

  productList = productList.map(product => {
    const mergedProduct = {
      ...product,
      ...fromEntities.getDetail(state, 'advertiserAdProduct', product.product_id),
    };

    mergedProduct.pt_value = mergedProduct.pt_value || 25;
    initialValues.product[product.product_id] = mergedProduct;

    return mergedProduct;
  });

  const creativeList = groupBy(
    fromEntities.getList(state, 'advertiserAdInhandCreative'),
    'product_id'
  );

  return {
    inhandVerify,
    productList,
    creativeList,
    loading: pending(state, 'advertiserAdInhandListRead'),
    visible: isOpen(modal, 'create-inhand-product'),
    initialValues,
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      params: { lineItemId },
    },
  }
) => ({
  clearCreativeEntities: () =>
    dispatch(
      resourceDeleteSuccess('advertiser-ad/:lineItemId/inhand/:id/creative', {
        needle: lineItemId,
      })
    ),
  getCreativeList: ({ product_id }) =>
    product_id
      ? dispatch(
          resourceListReadRequest(
            'advertiser-ad/:lineItemId/inhand/:id/creative',
            { page_size: 100 },
            { lineItemId, id: product_id }
          )
        )
      : null,
  getProduct: id =>
    dispatch(
      resourceDetailReadRequest('advertiser-ad/:lineItemId/product/:id', {
        lineItemId,
        id,
      })
    ),
  removeProduct: id =>
    dispatch(
      resourceDeleteRequest('advertiser-ad/:lineItemId/inhand/:id', {
        lineItemId,
        id,
      })
    ),
  getProductList: () =>
    dispatch(
      resourceListReadRequest('advertiser-ad/:id/inhand', { page_size: 100 }, { id: lineItemId })
    ),
  verifyInhand: () =>
    dispatch(resourceListReadRequest('advertiser-ad/:id/inhand-verify', {}, { id: lineItemId })),
  updateProduct: (id, data) =>
    dispatch(
      resourceUpdateRequest('advertiser-ad/:lineItemId/inhand/:id', { lineItemId, id }, data)
    ),
  showProductListModal: () => dispatch(modalShow('list-inhand-product')),
  showProductModal: product => {
    if (typeof product === 'object' && get(product, 'product_id', false) !== false) {
      dispatch(setEditRecord(product));
    }

    dispatch(modalShow('create-inhand-product'));
  },
  removeCreative: (productId, id) =>
    dispatch(
      resourceDeleteRequest('advertiser-ad/:lineItemId/inhand/:productId/creative/:id', {
        lineItemId,
        productId,
        id,
      })
    ),
  onCancel: () => {
    dispatch(modalHide('create-inhand-product'));

    // Timeout because hiding is a slow fade.
    // We don't want to flash the form resetting
    setTimeout(() => dispatch(emptyEditRecord()), 100);
  },
  showCreativeModal: product => {
    dispatch(setEditRecord(product));
    return dispatch(modalShow('create-inhand-product-creative'));
  },
});

const onSubmit = (values, dispatch, props) => {
  const productIds = Object.keys(values.product);
  const requests = [];
  forEach(productIds, id => {
    const updateValues = values.product[id];
    updateValues.pt_value = 25;
    requests.push(updateValues);
  });

  return Promise.all(requests.map(k => props.updateProduct(k.product_id, k)))
    .then(() => {
      notification.success({
        message: 'Success',
        description: 'Your products have been updated.',
        duration: 4,
      });
      props.verifyInhand();
    })
    .catch(e =>
      notification.error({
        message: 'Error',
        description: typeof e === 'string' ? e : 'Could not update product.',
        duration: 0,
      })
    );
};

const validate = createValidator({
  product: createValidator({
    pt_value: [required, integer, minSize(0)],
    budget: [required, integer, minSize(0)],
  }),
});

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  reduxForm({
    form: 'InHandTab',
    enableReinitialize: true,
    onSubmit,
    validate,
  })
)(InHandTabContainer);
